import L from 'leaflet';
import { LocateFixed, MapPin, Search, X } from 'lucide-react';
import React, { useEffect, useRef, useState } from 'react';

import 'leaflet/dist/leaflet.css';

/**
 * WhatsApp-style location picker for the chat composer.
 *
 * Leaflet + OpenStreetMap tiles - no API key, no billing. Tap (or drag the
 * pin) to choose a spot; the place name/address auto-fills via Nominatim
 * reverse geocoding and stays editable. Search also goes through Nominatim
 * (fired on Enter / button only - their usage policy dislikes per-keystroke
 * queries).
 */

export interface PickedLocation {
  latitude: number;
  longitude: number;
  name?: string;
  address?: string;
}

/** Indore - the office's home town; just the initial viewport, nothing more. */
const DEFAULT_CENTER: [number, number] = [22.7196, 75.8577];

/**
 * Default marker icons 404 under Vite (Leaflet resolves them relative to its
 * CSS), so draw our own WhatsApp-green pin as a divIcon instead.
 */
const PIN_ICON = L.divIcon({
  className: '',
  html: `<svg xmlns="http://www.w3.org/2000/svg" width="34" height="34" viewBox="0 0 24 24" fill="#128C7E" stroke="#fff" stroke-width="1.4"><path d="M20 10c0 6-8 12-8 12s-8-6-8-12a8 8 0 0 1 16 0Z"/><circle cx="12" cy="10" r="3" fill="#fff" stroke="none"/></svg>`,
  iconSize: [34, 34],
  iconAnchor: [17, 32],
});

interface NominatimResult {
  lat: string;
  lon: string;
  name?: string;
  display_name?: string;
}

const NOMINATIM = 'https://nominatim.openstreetmap.org';

const LocationPicker = ({
  onSend,
  onClose,
  sending,
}: {
  onSend: (location: PickedLocation) => void;
  onClose: () => void;
  sending: boolean;
}): React.JSX.Element => {
  const mapElRef = useRef<HTMLDivElement | null>(null);
  const mapRef = useRef<L.Map | null>(null);
  const markerRef = useRef<L.Marker | null>(null);

  const [picked, setPicked] = useState<{ latitude: number; longitude: number } | null>(null);
  const [name, setName] = useState('');
  const [address, setAddress] = useState('');
  const [query, setQuery] = useState('');
  const [busy, setBusy] = useState<'search' | 'locate' | null>(null);
  const [error, setError] = useState<string | null>(null);

  /** Drop/move the pin and reverse-geocode a human label for it. */
  const placePin = (lat: number, lng: number, opts?: { pan?: boolean }): void => {
    const map = mapRef.current;
    if (!map) return;
    setPicked({ latitude: lat, longitude: lng });
    setError(null);
    if (markerRef.current) {
      markerRef.current.setLatLng([lat, lng]);
    } else {
      markerRef.current = L.marker([lat, lng], { icon: PIN_ICON, draggable: true }).addTo(map);
      markerRef.current.on('dragend', () => {
        const at = markerRef.current!.getLatLng();
        placePin(at.lat, at.lng);
      });
    }
    if (opts?.pan) map.setView([lat, lng], Math.max(map.getZoom(), 15));

    // Best-effort label - a pin with no name still sends fine.
    void fetch(
      `${NOMINATIM}/reverse?format=jsonv2&lat=${lat}&lon=${lng}&zoom=17&addressdetails=0`,
      { headers: { Accept: 'application/json' } },
    )
      .then((r) => (r.ok ? (r.json() as Promise<NominatimResult>) : null))
      .then((r) => {
        if (!r) return;
        const label = r.display_name ?? '';
        setName(r.name ?? label.split(',')[0] ?? '');
        setAddress(label);
      })
      .catch(() => undefined);
  };

  useEffect(() => {
    if (!mapElRef.current || mapRef.current) return;
    const map = L.map(mapElRef.current, { zoomControl: true, attributionControl: true }).setView(
      DEFAULT_CENTER,
      12,
    );
    L.tileLayer('https://tile.openstreetmap.org/{z}/{x}/{y}.png', {
      maxZoom: 19,
      attribution: '&copy; OpenStreetMap',
    }).addTo(map);
    map.on('click', (e: L.LeafletMouseEvent) => placePin(e.latlng.lat, e.latlng.lng));
    mapRef.current = map;
    // The widget panel animates open; sizes are wrong until Leaflet re-measures.
    setTimeout(() => map.invalidateSize(), 50);
    return () => {
      map.remove();
      mapRef.current = null;
      markerRef.current = null;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const search = async (): Promise<void> => {
    const q = query.trim();
    if (!q || busy) return;
    setBusy('search');
    setError(null);
    try {
      const res = await fetch(
        `${NOMINATIM}/search?format=jsonv2&limit=1&q=${encodeURIComponent(q)}`,
        { headers: { Accept: 'application/json' } },
      );
      const rows = (await res.json()) as NominatimResult[];
      const hit = rows[0];
      if (!hit) {
        setError('No results - try a broader search or tap the map directly.');
        return;
      }
      placePin(Number(hit.lat), Number(hit.lon), { pan: true });
    } catch {
      setError('Search failed - tap the map to drop a pin instead.');
    } finally {
      setBusy(null);
    }
  };

  const useMyLocation = (): void => {
    if (!navigator.geolocation || busy) return;
    setBusy('locate');
    setError(null);
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        setBusy(null);
        placePin(pos.coords.latitude, pos.coords.longitude, { pan: true });
      },
      () => {
        setBusy(null);
        setError('Could not read your location - tap the map to drop a pin.');
      },
      { enableHighAccuracy: true, timeout: 8000 },
    );
  };

  return (
    <div className="mt-2 flex flex-col gap-1.5 rounded-md border border-emerald-200 bg-white p-2">
      <div className="flex items-center justify-between">
        <span className="flex items-center gap-1.5 text-xs font-semibold text-zinc-700">
          <MapPin className="h-3.5 w-3.5 text-emerald-600" /> Send a location
        </span>
        <button
          type="button"
          onClick={onClose}
          aria-label="Close location picker"
          title="Close"
          className="rounded p-1 text-zinc-400 hover:bg-zinc-100 hover:text-zinc-700"
        >
          <X className="h-4 w-4" />
        </button>
      </div>
      <div className="flex items-center gap-1.5">
        <input
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              e.preventDefault();
              void search();
            }
          }}
          placeholder="Search a place…"
          className="h-8 min-w-0 flex-1 rounded-md border border-zinc-200 px-2 text-xs"
        />
        <button
          type="button"
          onClick={() => void search()}
          disabled={busy !== null || !query.trim()}
          title="Search"
          aria-label="Search place"
          className="flex h-8 w-8 shrink-0 items-center justify-center rounded-md border border-zinc-200 text-zinc-600 hover:bg-zinc-50 disabled:opacity-50"
        >
          <Search className={`h-3.5 w-3.5 ${busy === 'search' ? 'animate-pulse' : ''}`} />
        </button>
        <button
          type="button"
          onClick={useMyLocation}
          disabled={busy !== null}
          title="Use my current location"
          aria-label="Use my current location"
          className="flex h-8 w-8 shrink-0 items-center justify-center rounded-md border border-zinc-200 text-zinc-600 hover:bg-zinc-50 disabled:opacity-50"
        >
          <LocateFixed className={`h-3.5 w-3.5 ${busy === 'locate' ? 'animate-pulse' : ''}`} />
        </button>
      </div>

      <div
        ref={mapElRef}
        className="h-52 w-full overflow-hidden rounded-md border border-zinc-200"
      />
      <p className="text-[10px] text-zinc-400">Tap the map to drop a pin - drag it to fine-tune.</p>
      {error && <p className="text-[11px] text-rose-600">{error}</p>}

      {picked && (
        <>
          <input
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="Place name (optional)"
            className="h-8 rounded-md border border-zinc-200 px-2 text-xs"
          />
          <input
            value={address}
            onChange={(e) => setAddress(e.target.value)}
            placeholder="Address (optional)"
            className="h-8 rounded-md border border-zinc-200 px-2 text-xs"
          />
        </>
      )}
      <button
        type="button"
        onClick={() =>
          picked &&
          onSend({
            ...picked,
            ...(name.trim() ? { name: name.trim() } : {}),
            ...(address.trim() ? { address: address.trim() } : {}),
          })
        }
        disabled={sending || !picked}
        className="flex h-8 items-center justify-center gap-1.5 rounded-md bg-[#25D366] text-xs font-semibold text-white shadow disabled:opacity-50"
      >
        <MapPin className="h-3.5 w-3.5" />
        {picked ? 'Send this location' : 'Pick a spot on the map'}
      </button>
    </div>
  );
};

export default LocationPicker;
